import { feedItems, statusMeta, type FeedStatus } from "@/mocks/news";
import FeedCard from "./FeedCard";

interface FeedSectionProps {
  activeStatus: FeedStatus | "全部";
  onStatusChange: (status: FeedStatus | "全部") => void;
  activeTopic: string;
  onClearTopic: () => void;
  query: string;
  onQueryChange: (value: string) => void;
}

const statusFilters: (FeedStatus | "全部")[] = ["全部", "突发", "追踪中", "已澄清", "已完结"];

export default function FeedSection({
  activeStatus,
  onStatusChange,
  activeTopic,
  onClearTopic,
  query,
  onQueryChange,
}: FeedSectionProps) {
  const keyword = query.trim();
  const visibleItems = feedItems.filter((item) => {
    const matchStatus = activeStatus === "全部" || item.status === activeStatus;
    const matchTopic = !activeTopic || item.category === activeTopic;
    const matchKeyword =
      !keyword ||
      item.title.includes(keyword) ||
      item.summary.includes(keyword) ||
      item.category.includes(keyword);
    return matchStatus && matchTopic && matchKeyword;
  });

  return (
    <section id="live" className="w-full">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <span className="flex items-center gap-2 text-xs font-semibold tracking-widest text-primary-600">
            <span className="live-dot h-1.5 w-1.5 rounded-full bg-primary-500"></span>
            LIVE FEED
          </span>
          <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            实时滚动时间线
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            按时间顺序整理正在发酵的消息，标注每一条的状态，点开就能看到完整进展。
          </p>
        </div>

        <div className="relative w-full lg:w-72">
          <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="搜索标题、关键词或分类"
            aria-label="搜索实时滚动内容"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {statusFilters.map((status) => {
          const active = activeStatus === status;
          return (
            <button
              key={status}
              type="button"
              onClick={() => onStatusChange(status)}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors ${
                active
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-200 hover:bg-primary-50"
              }`}
            >
              {status}
            </button>
          );
        })}
        {activeTopic && (
          <button
            type="button"
            onClick={onClearTopic}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-sm text-primary-700"
          >
            <i className="ri-price-tag-3-line"></i>
            {activeTopic}
            <i className="ri-close-circle-fill text-xs"></i>
          </button>
        )}
      </div>

      {activeStatus !== "全部" && (
        <p className="mt-3 text-xs text-foreground-500">
          「{activeStatus}」：{statusMeta[activeStatus].desc}
        </p>
      )}

      <div className="mt-8">
        {visibleItems.length > 0 ? (
          <div className="relative space-y-5 before:absolute before:bottom-4 before:left-[14px] before:top-6 before:w-px before:bg-background-300 sm:before:left-[18px]">
            {visibleItems.map((item, index) => (
              <FeedCard key={item.id} item={item} index={index} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-inbox-line text-2xl"></i>
            </span>
            <p className="text-sm text-foreground-700">没有找到匹配的进展</p>
            <p className="text-xs text-foreground-500">换个关键词，或点下面的按钮恢复全部内容</p>
            <button
              type="button"
              onClick={() => {
                onStatusChange("全部");
                onClearTopic();
                onQueryChange("");
              }}
              className="mt-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}